import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Switch,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
} from "@mui/material";
import LaptopIcon from "@mui/icons-material/Laptop";
import TabletIcon from "@mui/icons-material/Tablet";
import SecurityIcon from "@mui/icons-material/Security";
import LockIcon from "@mui/icons-material/Lock";
import LogoutIcon from "@mui/icons-material/Logout";
import ChangePasswordDialog from "./ChangePasswordDialog";
import Enable2FADialog from "./Enable2FADialog";
import { SecuritySettings } from "shared/types";

interface SecuritySectionProps {
  settings: SecuritySettings;
  onToggle2FA: () => void;
  onLoginAlertChange: (value: boolean) => void;
  onLogoutSession: (id: string) => void;
  onLogoutAllSessions: () => void;
}

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#178B8E" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#178B8E",
  },
};

const SecuritySection: React.FC<SecuritySectionProps> = ({
  settings,
  onToggle2FA,
  onLoginAlertChange,
  onLogoutSession,
  onLogoutAllSessions,
}) => {
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [twoFADialogOpen, setTwoFADialogOpen] = useState(false);

  const handlePasswordSave = (
    _currentPassword: string,
    _newPassword: string,
  ) => {
    // TODO: call API
    console.log("Password change requested");
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} mb={0.5}>
        Security
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Manage your account security settings
      </Typography>

      {/* Password */}
      <Box mb={3}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <LockIcon sx={{ color: "#178B8E", fontSize: 20 }} />
          <Typography variant="body1" fontWeight={600}>
            Password
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ letterSpacing: 2 }}
            >
              ••••••••••••
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last changed: Never
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setChangePasswordOpen(true)}
            sx={{
              textTransform: "none",
              borderColor: "#178B8E",
              color: "#178B8E",
            }}
          >
            Change Password
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Two-Factor Authentication */}
      <Box mb={3}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <SecurityIcon sx={{ color: "#178B8E", fontSize: 20 }} />
          <Typography variant="body1" fontWeight={600}>
            Two-Factor Authentication
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Chip
              label={settings.twoFactorEnabled ? "Enabled" : "Not Enabled"}
              size="small"
              sx={{
                mb: 0.5,
                backgroundColor: settings.twoFactorEnabled
                  ? "rgba(23,139,142,0.1)"
                  : "#F5F5F5",
                color: settings.twoFactorEnabled ? "#178B8E" : "text.secondary",
                fontWeight: 600,
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Add an extra layer of security to your account
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setTwoFADialogOpen(true)}
            sx={{
              textTransform: "none",
              borderColor: "#178B8E",
              color: "#178B8E",
            }}
          >
            {settings.twoFactorEnabled ? "Manage 2FA" : "Enable 2FA"}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Active Sessions */}
      <Box mb={3}>
        <Typography variant="body1" fontWeight={600} mb={1}>
          Active Sessions
        </Typography>
        <List disablePadding>
          {settings.activeSessions.map((session, index) => (
            <React.Fragment key={session.id}>
              {index > 0 && <Divider />}
              <ListItem
                disableGutters
                sx={{ py: 1 }}
                secondaryAction={
                  !session.current ? (
                    <IconButton
                      size="small"
                      onClick={() => onLogoutSession(session.id)}
                      title="Log out this session"
                    >
                      <LogoutIcon
                        fontSize="small"
                        sx={{ color: "error.main" }}
                      />
                    </IconButton>
                  ) : undefined
                }
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {session.current ? (
                    <LaptopIcon sx={{ color: "#178B8E", fontSize: 22 }} />
                  ) : (
                    <TabletIcon
                      sx={{ color: "text.secondary", fontSize: 22 }}
                    />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2">{session.device}</Typography>
                      {session.current && (
                        <Chip
                          label="Current"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.65rem",
                            backgroundColor: "rgba(23,139,142,0.1)",
                            color: "#178B8E",
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    session.lastActive
                      ? `Last active ${session.lastActive}`
                      : "Active now"
                  }
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
        <Button
          variant="text"
          size="small"
          onClick={onLogoutAllSessions}
          sx={{ textTransform: "none", color: "error.main", mt: 0.5, pl: 0 }}
        >
          Log out all other sessions
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Login Alerts */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="body2" fontWeight={500}>
            Login Alerts
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Email me when someone logs into my account
          </Typography>
        </Box>
        <Switch
          checked={settings.loginAlerts}
          onChange={(e) => onLoginAlertChange(e.target.checked)}
          size="small"
          sx={switchSx}
        />
      </Box>

      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        onSave={handlePasswordSave}
      />
      <Enable2FADialog
        open={twoFADialogOpen}
        onClose={() => setTwoFADialogOpen(false)}
        onEnable={onToggle2FA}
      />
    </Paper>
  );
};

export default SecuritySection;
